import { Link } from '@tanstack/react-router'
import { TagBadge } from './TagBadge'

interface TagBadgeLinkProps {
    org: string
    tag: { uid?: string; name?: string; code?: string; color?: string; icon?: string }
    onRemove?: () => void
    compact?: boolean
    className?: string
}

/**
 * Wraps a TagBadge in a TanStack Link that navigates to the tag detail page
 * under settings (/$org/settings/tags/$tagId). Prefers the tag `code` when
 * present, falls back to the uid. When neither is set, renders a bare
 * TagBadge so the UI does not collapse.
 *
 * The remove button inside TagBadge calls preventDefault + stopPropagation,
 * so clicking the × does NOT trigger navigation. Composition pattern (§4
 * option B of specs/done/2026/2026-04/2026-04-22-02-tags-browse-page.md).
 */
export function TagBadgeLink({ org, tag, onRemove, compact, className }: TagBadgeLinkProps) {
    const target = tag.code || tag.uid
    if (!target) {
        return <TagBadge tag={{ uid: tag.uid || '', name: tag.name || '', color: tag.color, icon: tag.icon }} onRemove={onRemove} compact={compact} className={className} />
    }
    return (
        <Link
            to="/$org/settings/tags/$tagId"
            params={{ org, tagId: target }}
            className="inline-flex"
            data-testid={`tag-badge-link-${tag.code || tag.uid}`}
        >
            <TagBadge
                tag={{ uid: tag.uid || '', name: tag.name || '', color: tag.color, icon: tag.icon }}
                onRemove={onRemove}
                compact={compact}
                className={`cursor-pointer hover:opacity-80 ${className || ''}`}
            />
        </Link>
    )
}
